import React, { Suspense, useCallback, useEffect, useState } from "react";
import { Box, Paper, Stack, Table, Title, CloseButton } from "@mantine/core";
import classes from "./LegendPanel.module.css";
import { PANEL_TOGGLE_ANIMATION_DURATION } from "config/constants";
import { LEGENDS } from "config/legends";
import { useTranslation } from "react-i18next";
import { updateNonSaveableOptions } from "stores/optionsStore/actions";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { MapID } from "config/enums/mapIDs";

const PANEL_WIDTH = 250;

export const LegendPanel = () => {
  const { state, dispatch } = useOptions();
  const { t } = useTranslation();
  const CurrentLegendBody = LEGENDS[state.mapType];
  // The legend body (code and ~100 symbol images) is loaded only once the panel is opened for the first time,
  // then it stays rendered so the closing animation isn't cut short
  const [wasOpened, setWasOpened] = useState(state.isLegendOpen);
  if (state.isLegendOpen && !wasOpened) {
    setWasOpened(true);
  }

  const closeLegendPanel = useCallback(() => {
    dispatch(updateNonSaveableOptions({ isLegendOpen: false }));
  }, [dispatch]);

  useEffect(() => {
    if (!CurrentLegendBody) closeLegendPanel();
  }, [CurrentLegendBody, closeLegendPanel, state.mapType]);

  return CurrentLegendBody ? (
    // Over the map and the chart, which keep their width. Hidden when closed, so its shadow doesn't show at the
    // edge and its close button can't be focused
    <Stack
      gap={4}
      className={classes.panel}
      mod={{ open: state.isLegendOpen }}
      style={{
        "--panel-width": `${PANEL_WIDTH}px`,
        "--panel-transition-duration": `${PANEL_TOGGLE_ANIMATION_DURATION}s`,
      }}
    >
      <Paper
        shadow="lg"
        p={0}
        radius={0}
        className={classes.paper}
        mod={{ tron: state.mapType === MapID.tron }}
      >
        {/* The table scrolls in the space left under the title. A fixed max height (e.g. 96vh)
            overflowed the panel on mobile, where vh includes the browser toolbars, and cut off the last rows */}
        <Box
          pl="md"
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Title order={3} style={{ paddingTop: "5px", paddingBottom: "5px" }}>
            {t("map_controls.legend_button_tooltip")}
          </Title>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Table
              className={classes.table}
              style={{
                color: state.mapType === MapID.tron ? "white" : "inherit",
              }}
            >
              {wasOpened ? (
                <Suspense fallback={null}>
                  <CurrentLegendBody />
                </Suspense>
              ) : null}
            </Table>
          </div>
        </Box>
        <CloseButton
          className={classes.close}
          mod={{ tron: state.mapType === MapID.tron }}
          onClick={closeLegendPanel}
          variant="transparent"
        />
      </Paper>
    </Stack>
  ) : null;
};
